import React, { useCallback, useEffect, useState } from 'react';
import { Sun, SunMedium, CloudSun, Cloud, CloudLightning, HeartHandshake, Loader2, Mail, RefreshCw, AlertTriangle, Users } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { adminGet, adminPost, reportError, formatDate } from './adminApi';

const MOOD_UI = {
  sun: { label: 'Grand soleil', icon: Sun, color: 'text-[hsl(var(--brand-apricot))]', bg: 'bg-[hsl(var(--brand-apricot))]/15' },
  sun_light: { label: 'Soleil', icon: SunMedium, color: 'text-[hsl(var(--brand-apricot))]/80', bg: 'bg-[hsl(var(--brand-apricot))]/10' },
  cloud_sun: { label: 'Nuage et soleil', icon: CloudSun, color: 'text-[hsl(var(--primary))]', bg: 'bg-[hsl(var(--primary))]/10' },
  cloud: { label: 'Nuage', icon: Cloud, color: 'text-muted-foreground', bg: 'bg-secondary' },
  storm: { label: 'Éclairs', icon: CloudLightning, color: 'text-destructive', bg: 'bg-destructive/10' },
};
const PERIODS = [7, 14, 30];
const fmtDay = (d) => new Date(d + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
const barColor = (avg) => (avg == null ? 'bg-border' : avg >= 3.5 ? 'bg-[hsl(var(--brand-apricot))]' : avg >= 2.5 ? 'bg-[hsl(var(--primary))]' : avg >= 1.5 ? 'bg-muted-foreground' : 'bg-destructive');

/** Admin: daily mood weather of the Easers — today's answers, alerts (cloud/storm) with texts, trend and per-Easer averages. */
export function AdminEaserMoral({ adminKey }) {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [view, setView] = useState('today'); // today | alerts | easers

  const load = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/easer/moral', adminKey, { days });
      setData(res.data);
    } catch (err) {
      reportError('météo du moral', err, () => toast.error('Impossible de charger la météo du moral'));
      setData((d) => d || { today_answers: [], alerts: [], daily: [], easers: [], missing_today: [] });
    }
  }, [adminKey, days]);

  useEffect(() => { load(); }, [load]);

  const recapNow = async () => {
    setSending(true);
    try {
      const res = await adminPost('/api/admin/easer/moral/recap-now', adminKey, {}, {});
      if (res.data.sent) toast.success(`Récapitulatif envoyé (${res.data.answers} réponse${res.data.answers > 1 ? 's' : ''})`);
      else toast.warning(`Récapitulatif non envoyé : ${res.data.error || 'e-mail agence / SMTP à vérifier'}`);
    } catch (err) {
      reportError('récap moral', err, (m) => toast.error(m || 'Échec'));
    } finally {
      setSending(false);
    }
  };

  const answered = data?.today_answers?.length || 0;
  const total = data?.employees_total || 0;
  const todayCounts = data?.daily?.[0]?.counts;

  return (
    <Card className="p-5" data-testid="admin-easer-moral">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><HeartHandshake className="h-4 w-4" /> Météo du moral des Easers</h3>
          <p className="text-sm text-muted-foreground">Réponse quotidienne « Comment te sens-tu aujourd'hui ? ». Nuage et éclairs déclenchent une alerte e-mail immédiate ; récapitulatif chaque soir à 19h30.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border p-0.5">
            {PERIODS.map((p) => <button key={p} type="button" onClick={() => setDays(p)} data-testid={`moral-period-${p}`} className={`px-2.5 h-8 rounded-md text-xs font-medium ${days === p ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:text-foreground'}`}>{p} j</button>)}
          </div>
          <Button size="sm" variant="outline" onClick={load} aria-label="Actualiser" data-testid="moral-refresh"><RefreshCw className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={recapNow} disabled={sending} data-testid="moral-recap-now">{sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />} Envoyer le récap maintenant</Button>
        </div>
      </div>

      {data === null ? <Skeleton className="h-40" /> : (
        <div className="space-y-5">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="moral-kpis">
            <div className="rounded-xl border border-border p-3">
              <p className="text-xs text-muted-foreground">Réponses aujourd'hui</p>
              <p className="text-2xl font-semibold" data-testid="moral-kpi-answered">{answered}<span className="text-sm text-muted-foreground font-normal"> / {total}</span></p>
            </div>
            <div className="rounded-xl border border-border p-3">
              <p className="text-xs text-muted-foreground">Moyenne aujourd'hui</p>
              <p className="text-2xl font-semibold" data-testid="moral-kpi-avg-today">{data.daily?.[0]?.average != null ? `${data.daily[0].average.toFixed(1)}/5` : '–'}</p>
            </div>
            <div className="rounded-xl border border-border p-3">
              <p className="text-xs text-muted-foreground">Moyenne sur {days} jours</p>
              <p className="text-2xl font-semibold" data-testid="moral-kpi-avg">{data.average != null ? `${data.average.toFixed(1)}/5` : '–'}</p>
            </div>
            <div className="rounded-xl border border-border p-3">
              <p className="text-xs text-muted-foreground">Alertes (nuage / éclairs)</p>
              <p className={`text-2xl font-semibold ${data.alerts.length ? 'text-destructive' : ''}`} data-testid="moral-kpi-alerts">{data.alerts.length}</p>
            </div>
          </div>

          {todayCounts && (
            <div className="flex flex-wrap gap-2 text-sm" data-testid="moral-today-counts">
              {Object.entries(MOOD_UI).map(([k, m]) => <span key={k} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${m.bg}`}><m.icon className={`h-4 w-4 ${m.color}`} /> {m.label} <b>{todayCounts[k]}</b></span>)}
            </div>
          )}

          {/* Trend */}
          <div data-testid="moral-trend">
            <p className="text-xs font-semibold text-muted-foreground mb-2">Tendance ({days} jours, moyenne /5)</p>
            <div className="flex items-end gap-1 h-20">
              {[...data.daily].reverse().map((d) => (
                <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-0.5 min-w-0" title={`${fmtDay(d.day)} : ${d.average != null ? d.average.toFixed(1) + '/5' : 'aucune réponse'} (${d.answers} rép.)`}>
                  <div className={`w-full rounded-t ${barColor(d.average)}`} style={{ height: `${d.average != null ? Math.max(8, (d.average / 5) * 64) : 4}px` }} />
                  {days <= 14 && <span className="text-[9px] text-muted-foreground truncate w-full text-center">{new Date(d.day + 'T12:00:00').getDate()}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Views */}
          <div className="flex rounded-lg border border-border p-0.5 w-fit">
            {[['today', `Aujourd'hui (${answered})`], ['alerts', `Alertes (${data.alerts.length})`], ['easers', `Par Easer (${data.easers.length})`]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setView(id)} data-testid={`moral-view-${id}`} className={`px-3 h-9 rounded-md text-sm font-medium ${view === id ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:text-foreground'}`}>{label}</button>
            ))}
          </div>

          {view === 'today' && (
            <div className="space-y-2" data-testid="moral-today-list">
              {data.today_answers.length === 0 && <p className="text-sm text-muted-foreground">Aucune réponse pour l'instant aujourd'hui.</p>}
              {data.today_answers.map((a) => <MoodRow key={a.id} a={a} />)}
              {data.missing_today.length > 0 && (
                <p className="text-xs text-muted-foreground flex items-start gap-1 pt-1" data-testid="moral-missing"><Users className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> Sans réponse aujourd'hui : {data.missing_today.map((e) => e.name).join(', ')}</p>
              )}
            </div>
          )}

          {view === 'alerts' && (
            <div className="space-y-2" data-testid="moral-alerts-list">
              {data.alerts.length === 0 && <p className="text-sm text-muted-foreground">Aucun nuage ni éclair sur la période.</p>}
              {data.alerts.map((a) => <MoodRow key={a.id} a={a} showDay />)}
            </div>
          )}

          {view === 'easers' && (
            <ul className="divide-y divide-border" data-testid="moral-easers-list">
              {data.easers.length === 0 && <li className="text-sm text-muted-foreground py-2">Aucune réponse sur la période.</li>}
              {data.easers.map((e) => {
                const last = e.last ? MOOD_UI[e.last.mood] : null;
                return (
                  <li key={e.user_id} className="py-2 flex items-center gap-3 text-sm" data-testid="moral-easer-row">
                    <span className="font-medium flex-1 min-w-0 truncate">{e.user_name}</span>
                    {e.alerts > 0 && <Badge variant="outline" className="text-destructive border-destructive/40"><AlertTriangle className="h-3 w-3 mr-1" /> {e.alerts}</Badge>}
                    <span className="text-muted-foreground">{e.answers} rép.</span>
                    <span className="font-semibold w-14 text-right">{e.average.toFixed(1)}/5</span>
                    {last && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground w-28" title={`Dernière réponse : ${fmtDay(e.last.day)}`}><last.icon className={`h-4 w-4 ${last.color}`} /> {fmtDay(e.last.day)}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function MoodRow({ a, showDay = false }) {
  const m = MOOD_UI[a.mood];
  const alert = a.mood === 'cloud' || a.mood === 'storm';
  return (
    <div className={`rounded-xl border p-3 flex items-start gap-3 ${alert ? 'border-destructive/30 bg-destructive/5' : 'border-border'}`} data-testid="moral-row">
      <span className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${m.bg}`}><m.icon className={`h-5 w-5 ${m.color}`} /></span>
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-medium flex flex-wrap items-center gap-x-2">{a.user_name} <span className={`text-xs ${m.color}`}>{m.label}</span>
          <span className="text-xs text-muted-foreground">{showDay ? fmtDay(a.day) + ' · ' : ''}{formatDate(a.updated_at || a.created_at, true)}</span>
          {alert && <span className="text-xs text-muted-foreground">{a.alert_sent ? '· alerte e-mail envoyée' : '· alerte e-mail non envoyée'}</span>}
        </p>
        {a.feeling && <p className="mt-1"><span className="text-muted-foreground">Ressenti :</span> {a.feeling}</p>}
        {a.improve && <p className="mt-0.5"><span className="text-muted-foreground">Amélioration souhaitée :</span> {a.improve}</p>}
      </div>
    </div>
  );
}
